import { jsxs, jsx } from 'react/jsx-runtime';
import { useRef, useState, useEffect } from 'react';

/**
 * radial-color-picker/color-wheel v2.2.4
 *
 * https://github.com/radial-color-picker/color-wheel
 *
 * Copyright (c) 2018-present, Rosen Kanev
 * Released under the MIT License.
 */

/**
 * Modified version of Lea Verou's
 * {@link https://github.com/leaverou/conic-gradient conic-gradient}.
 *
 * @example
 * paintColorWheelToCanvas(document.querySelector('#canvas'), 250);
 *
 * @param   {HTMLCanvasElement} canvas Canvas to paint the color wheel
 * @param   {Number}            size   Color wheel radius in pixels
 * @returns {HTMLCanvasElement} canvas The passed canvas for easier chaining
 */
function paintColorWheelToCanvas(canvas, size) {
  const half = size / 2;
  const radius = Math.sqrt(2) * half;
  const deg = Math.PI / 180;
  const pi2 = Math.PI * 2;
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d'); // .02: To prevent empty blank line and corresponding moire
  // only non-alpha colors are cared now

  const thetaOffset = 0.5 * deg + 0.02; // Transform coordinate system so that angles start from the top left, like in CSS

  ctx.translate(half, half);
  ctx.rotate(-Math.PI / 2);
  ctx.translate(-half, -half);

  for (let i = 0; i < 360; i += 0.5) {
    ctx.fillStyle = "hsl(" + i + ", 100%, 50%)";
    ctx.beginPath();
    ctx.moveTo(half, half);
    const beginArg = i * deg;
    const endArg = Math.min(pi2, beginArg + thetaOffset);
    ctx.arc(half, half, radius, beginArg, endArg);
    ctx.closePath();
    ctx.fill();
  }

  return canvas;
}

/**
 * radial-color-picker/rotator v3.0.2
 *
 * https://github.com/radial-color-picker/rotator
 *
 * Copyright (c) 2018-present, Rosen Kanev
 * Released under the MIT License.
 */
const TO_DEGREES = 180 / Math.PI;

const normalizeAngle = angle => {
  const mod = angle % 360;
  return mod < 0 ? 360 + mod : mod;
};

const getRotationFromCoords = ({
  x,
  y
}, rect) => {
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  return Math.atan2(y - cy, x - cx) * TO_DEGREES;
};

const noop = () => {};
/**
 * Modified version of Denis Radin's
 * {@link https://github.com/PixelsCommander/Propeller Propeller}.
 */


class Rotator {
  constructor(element, options) {
    this.active = false;
    this.element = element;
    this.element.style.willChange = 'transform';
    this.initOptions(options);
    this.updateCSS();
    this.bindHandlers();
    this.addListeners();
  }

  get angle() {
    return this._angle;
  }

  set angle(value) {
    if (this._angle !== value) {
      this._angle = normalizeAngle(value);
      this.updateCSS();
    }
  }

  initOptions(options) {
    options = options || {};
    this.onRotate = options.onRotate || noop;
    this.onDragStart = options.onDragStart || noop;
    this.onDragStop = options.onDragStop || noop;
    this._angle = options.angle || 0;
  }

  bindHandlers() {
    this.onRotationStart = this.onRotationStart.bind(this);
    this.onRotated = this.onRotated.bind(this);
    this.onRotationStop = this.onRotationStop.bind(this);
  }

  addListeners() {
    this.element.addEventListener('touchstart', this.onRotationStart, {
      passive: true
    });
    document.addEventListener('touchmove', this.onRotated, {
      passive: false
    });
    document.addEventListener('touchend', this.onRotationStop, {
      passive: true
    });
    document.addEventListener('touchcancel', this.onRotationStop, {
      passive: true
    });
    this.element.addEventListener('mousedown', this.onRotationStart, {
      passive: true
    });
    document.addEventListener('mousemove', this.onRotated, {
      passive: false
    });
    document.addEventListener('mouseup', this.onRotationStop, {
      passive: true
    });
    document.addEventListener('mouseleave', this.onRotationStop, {
      passive: false
    });
  }

  removeListeners() {
    this.element.removeEventListener('touchstart', this.onRotationStart);
    document.removeEventListener('touchmove', this.onRotated);
    document.removeEventListener('touchend', this.onRotationStop);
    document.removeEventListener('touchcancel', this.onRotationStop);
    this.element.removeEventListener('mousedown', this.onRotationStart);
    document.removeEventListener('mousemove', this.onRotated);
    document.removeEventListener('mouseup', this.onRotationStop);
    document.removeEventListener('mouseleave', this.onRotationStop);
  }

  destroy() {
    this.onRotationStop();
    this.removeListeners();
  }

  onRotationStart(event) {
    if (event.type === 'touchstart' || event.button === 0) {
      this.active = true;
      this.onDragStart(event);
      this.setAngleFromEvent(event);
    }
  }

  onRotationStop() {
    if (this.active) {
      this.active = false;
      this.onDragStop();
    }

    this.active = false;
  }

  onRotated(event) {
    if (this.active) {
      event.preventDefault();
      this.setAngleFromEvent(event);
    }
  }

  setAngleFromEvent(event) {
    const ev = event.targetTouches ? event.targetTouches[0] : event;
    const newAngle = getRotationFromCoords({
      x: ev.clientX,
      y: ev.clientY
    }, this.element.getBoundingClientRect()); // atan2 gives values between -180 to 180 deg
    // add 90 degrees offset so that it starts from 0 deg (or red)
    // and then normalize negative values

    this._angle = normalizeAngle(newAngle + 90);
    this.updateCSS();
    this.onRotate(this._angle);
  }

  updateCSS() {
    this.element.style.transform = "rotate(" + this._angle + "deg)";
  }

}

const noop$1 = () => {};

const colors = ['red', 'yellow', 'green', 'cyan', 'blue', 'magenta', 'red'];
const keys = {
  ArrowUp: (oldAngle, step) => oldAngle + step,
  ArrowRight: (oldAngle, step) => oldAngle + step,
  ArrowDown: (oldAngle, step) => oldAngle - step,
  ArrowLeft: (oldAngle, step) => oldAngle - step,
  PageUp: (oldAngle, step) => oldAngle + step * 10,
  PageDown: (oldAngle, step) => oldAngle - step * 10,
  Home: () => 0,
  End: () => 359
};

const ColorPicker = ({
  hue = 0,
  saturation = 100,
  luminosity = 50,
  alpha = 1,
  disabled = false,
  step = 1,
  variant = 'collapsible',
  // collapsible | persistent
  initiallyCollapsed = false,
  mouseScroll = false,
  ariaLabelColorWell = 'color well',
  onInput = noop$1,
  onChange = noop$1,
  onSelect = noop$1,
  className,
  ...rest
}) => {
  const paletteRef = useRef(null);
  const rotatorRef = useRef(null);
  const elRef = useRef(null);
  const rotator = useRef(null);
  const angleRef = useRef(hue); // set the SSR value just once when the component is created
  // prevents knob jumping when using Server Side Rendering
  // where the knob's position is updated only after the client-side code is executed (on mount)

  const [ssrHue, setSsrHue] = useState(hue);
  const [isKnobIn, setIsKnobIn] = useState(!initiallyCollapsed);
  const [isPaletteIn, setIsPaletteIn] = useState(!initiallyCollapsed);
  const [isPressed, setIsPressed] = useState(false);
  const [isRippling, setIsRippling] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  useEffect(() => {
    if (mouseScroll) {
      rotatorRef.current.addEventListener('wheel', onScroll);
    }

    if (process.env.NODE_ENV === 'development' && initiallyCollapsed && variant === 'persistent') {
      console.warn("Incorrect config: using variant=\"persistent\" and initiallyCollapsed={true} at the same time is not supported.");
    }

    const isConicGradientSupported = getComputedStyle(paletteRef.current).backgroundImage.includes('conic');

    if (!isConicGradientSupported) {
      paintColorWheelToCanvas(paletteRef.current.firstElementChild, elRef.current.offsetWidth || 280);
    }

    rotator.current = new Rotator(rotatorRef.current, {
      angle: angleRef.current,

      onRotate(value) {
        angleRef.current = value;
        onInput(value);
      },

      onDragStart() {
        setIsDragging(true);
      },

      onDragStop() {
        setIsDragging(false);
        onChange(angleRef.current);
      }

    });
    return () => {
      rotator.current.destroy();
      rotator.current = null;

      if (mouseScroll) {
        rotatorRef.current.removeEventListener('wheel', onScroll);
      }
    };
  }, []);
  useEffect(() => {
    angleRef.current = hue;
    rotator.current.angle = hue;
  }, [hue]);

  const onScroll = ev => {
    if (isPressed || !isKnobIn) return;
    ev.preventDefault();
    const newAngle = ev.deltaY > 0 ? rotator.current.angle + step : rotator.current.angle - step;
    rotator.current.angle = newAngle;
    angleRef.current = newAngle;
    onInput(newAngle);
    onChange(newAngle);
  };

  const onKeyUp = ev => {
    if (ev.key === 'Enter' && ev.target === elRef.current) {
      selectColor();
    }
  };

  const onKeyDown = ev => {
    if (disabled || isPressed || !isKnobIn || !(ev.key in keys)) return;
    ev.preventDefault();
    const newAngle = keys[ev.key](rotator.current.angle, step);
    rotator.current.angle = newAngle;
    angleRef.current = newAngle;
    onInput(newAngle);
    onChange(newAngle);
  };

  const selectColor = () => {
    setIsPressed(true);

    if (isPaletteIn && isKnobIn) {
      onSelect(angleRef.current);
      setIsRippling(true);
    } else {
      setIsPaletteIn(true);
    }
  };

  const togglePicker = () => {
    if (variant !== 'persistent') {
      if (isKnobIn) {
        setIsKnobIn(false);
      } else {
        setIsKnobIn(true);
        setIsPaletteIn(true);
      }
    }

    setIsRippling(false);
    setIsPressed(false);
  };

  const hidePalette = () => {
    if (!isKnobIn) {
      setIsPaletteIn(false);
    }
  };

  const color = "hsla(" + angleRef.current + ", " + saturation + "%, " + luminosity + "%, " + alpha + ")";
  const wrapperClassNames = ['rcp', className, isDragging && 'dragging', disabled && 'disabled'].filter(Boolean).join(' ');
  return /*#__PURE__*/jsxs("div", {
    "aria-roledescription": "radial slider",
    "aria-label": "color picker",
    "aria-valuetext": colors[Math.round(angleRef.current / 60)],
    ...rest,
    "aria-expanded": isPaletteIn,
    "aria-valuenow": angleRef.current,
    "aria-disabled": disabled,
    "aria-valuemin": 0,
    "aria-valuemax": 359,
    ref: elRef,
    role: "slider",
    className: wrapperClassNames,
    tabIndex: disabled ? -1 : 0,
    onKeyUp: onKeyUp,
    onKeyDown: onKeyDown,
    children: [/*#__PURE__*/jsx("div", {
      ref: paletteRef,
      className: "rcp__palette " + (isPaletteIn ? 'in' : 'out'),
      children: /*#__PURE__*/jsx("canvas", {})
    }), /*#__PURE__*/jsx("div", {
      ref: rotatorRef,
      className: "rcp__rotator",
      style: {
        pointerEvents: disabled || isPressed || !isKnobIn ? 'none' : null,
        transform: "rotate(" + ssrHue + "deg)"
      },
      children: /*#__PURE__*/jsx("div", {
        className: "rcp__knob " + (isKnobIn ? 'in' : 'out'),
        onTransitionEnd: hidePalette
      })
    }), /*#__PURE__*/jsx("div", {
      className: ("rcp__ripple " + (isRippling ? 'rippling' : '')).trim(),
      style: {
        borderColor: color
      }
    }), /*#__PURE__*/jsx("button", {
      type: "button",
      className: ("rcp__well " + (isPressed ? 'pressed' : '')).trim(),
      style: {
        backgroundColor: color
      },
      "aria-label": ariaLabelColorWell,
      disabled: disabled,
      tabIndex: disabled ? -1 : 0,
      onAnimationEnd: togglePicker,
      onClick: selectColor
    })]
  });
};

export default ColorPicker;
